import { MRT_ColumnDef } from 'material-react-table';
import { ColumnSizing } from '../tables-context';
import { TableCell } from '../../../components/table/TableCell';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck } from '@fortawesome/free-solid-svg-icons';
import { ProviderLayout } from '../../../components/provider/ProviderLayout';
import { ProviderCardBrands } from '../../../components/provider/ProviderCardBrands';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { TableProgressBar } from '../../../components/provider/TableProgressBar';
import { VALUE_TYPE } from '../../../components/general-components/NumberFormatterComponent';
import { IProviderCycleMetrics, IProviderProps, IRelatedMerchants } from '../../payments/payments-interfaces-helper';
import { RelatedMerchants } from '../../../components/provider/RelatedMerchants';

export enum ProviderTableAccessorKeys {
	name = 'name',
	id = 'id',
	authenticationType = 'authenticationType',
	isActive = 'isActive',
	sandbox = 'sandbox',
	merchantNumber = 'merchantNumber',
	mcc = 'mcc',
	masterCardDescriptorPrefix = 'masterCardDescriptorPrefix',
	visaDescriptorPrefix = 'visaDescriptorPrefix',
	processorId = 'processorId',
	fixedDescriptorPrefix = 'fixedDescriptorPrefix',
	cardBrands = 'cardBrands',
	masterCard_MonthlyMaxTransactionsCount = 'masterCard_MonthlyMaxTransactionsCount',
	masterCard_MonthlyMaxValue = 'masterCard_MonthlyMaxValue',
	visa_MonthlyMaxTransactionsCount = 'visa_MonthlyMaxTransactionsCount',
	visa_MonthlyMaxValue = 'visa_MonthlyMaxValue',
	supportCIT = 'supportCIT',
	supportMIT = 'supportMIT',
	providerLogo = 'providerLogo',
	partner = 'partnerId',
	totalMonthlyAmount = 'totalMonthlyMaxTransactionsValue',
	totalMonthlyCount = 'totalMonthlyMaxTransactionsCount',
	totalDailyAmount = 'totalDailyMaxTransactionsValue',
	totalDailyCount = 'totalDailyMaxTransactionsCount',
	relatedMerchantsCount = 'relatedMerchantsCount',
}

export const defaultProviderColumnSizing: ColumnSizing = {
	[ProviderTableAccessorKeys.providerLogo]: 50,
	[ProviderTableAccessorKeys.name]: 90,
	[ProviderTableAccessorKeys.merchantNumber]: 50,
	[ProviderTableAccessorKeys.relatedMerchantsCount]: 50,
	[ProviderTableAccessorKeys.processorId]: 70,
	[ProviderTableAccessorKeys.partner]: 90,
	[ProviderTableAccessorKeys.cardBrands]: 90,
	[ProviderTableAccessorKeys.totalMonthlyAmount]: 75,
	[ProviderTableAccessorKeys.totalMonthlyCount]: 75,
	[ProviderTableAccessorKeys.totalDailyAmount]: 75,
	[ProviderTableAccessorKeys.totalDailyCount]: 75,
	[ProviderTableAccessorKeys.masterCard_MonthlyMaxTransactionsCount]: 75,
	[ProviderTableAccessorKeys.masterCard_MonthlyMaxValue]: 75,
	[ProviderTableAccessorKeys.visa_MonthlyMaxTransactionsCount]: 75,
	[ProviderTableAccessorKeys.visa_MonthlyMaxValue]: 75,
	[ProviderTableAccessorKeys.supportCIT]: 10,
	[ProviderTableAccessorKeys.supportMIT]: 10,
	[ProviderTableAccessorKeys.isActive]: 10,
	[ProviderTableAccessorKeys.sandbox]: 10,
};

export interface IFormattedProviderProps {
	name: string,
	id: string,
	authenticationType: string,
	isActive: boolean,
	sandbox: boolean,
	mcc: string | number | null,
	merchantNumber: string | number | null,
	masterCardDescriptorPrefix: string,
	visaDescriptorPrefix: string | null,
	partnerId: string,
	processorId: string,
	fixedDescriptorPrefix: string | null,
	cardBrands: string,
	supportedCountries: string,
	supportedCurrencies: string,
	masterCard_MonthlyMaxTransactionsCount: number,
	masterCard_MonthlyMaxValue: number,
	visa_MonthlyMaxTransactionsCount: number,
	visa_MonthlyMaxValue: number,
	visaCurrentTransactionsCount: number,
	visaCurrentTransactionsValue: number,
	totalMonthlyMaxTransactionsValue: number,
	totalMonthlyMaxTransactionsCount: number,
	totalDailyMaxTransactionsValue: number,
	totalDailyMaxTransactionsCount: number,
	supportCIT: boolean,
	supportMIT: boolean,
	providerLogo?: string,
	providerLogoAlt?: string,
	cycleMetrics: IProviderCycleMetrics,
	useDynamicDescriptor: boolean,
	supportUDF: boolean,
	supportDynamicDescriptorUrl: boolean,
	supportDynamicDescriptorAddress: boolean,
	useDynamicDescriptorSuffix: boolean,
	processorPlatform: string;
	relatedMerchants: IRelatedMerchants[];
	relatedMerchantsCount: number;
	caid: string,
	bin: string,
	sponsorBank: string,
	tier: string,
	contactEmail: string,
	contactName: string,
	contactPhone: string,
	quarantine_AddedOn: string,
	configuration: string,
}

export const formattedProviderData = (data: IProviderProps[]): IFormattedProviderProps[] => data?.map(el => ({
	name: el?.name,
	id: el?.id,
	authenticationType: el?.authenticationType,
	isActive: el?.isActive,
	sandbox: el?.sandbox,
	mcc: el?.mcc,
	merchantNumber: el?.merchantNumber,
	masterCardDescriptorPrefix: el?.masterCardDescriptorPrefix,
	visaDescriptorPrefix: el?.visaDescriptorPrefix,
	partnerId: el?.partnerId,
	processorId: el?.processorId,
	fixedDescriptorPrefix: el?.fixedDescriptorPrefix,
	cardBrands: el?.capabilities?.cardBrands,
	supportedCountries: el?.capabilities?.supportedCountries,
	supportedCurrencies: el?.capabilities?.supportedCurrencies,
	masterCard_MonthlyMaxTransactionsCount: el?.capabilities?.masterCard_MonthlyMaxTransactionsCount,
	masterCard_MonthlyMaxValue: el?.capabilities?.masterCard_MonthlyMaxValue,
	visa_MonthlyMaxTransactionsCount: el?.capabilities?.visa_MonthlyMaxTransactionsCount,
	visa_MonthlyMaxValue: el?.capabilities?.visa_MonthlyMaxValue,
	visaCurrentTransactionsCount: el?.cycleMetrics?.visaCurrentTransactionsCount,
	visaCurrentTransactionsValue: el?.cycleMetrics?.visaCurrentTransactionsValue,
	totalMonthlyMaxTransactionsValue: el?.capabilities?.totalMonthlyMaxTransactionsValue,
	totalMonthlyMaxTransactionsCount: el?.capabilities?.totalMonthlyMaxTransactionsCount,
	totalDailyMaxTransactionsValue: el?.capabilities?.totalDailyMaxTransactionsValue,
	totalDailyMaxTransactionsCount: el?.capabilities?.totalDailyMaxTransactionsCount,
	supportCIT: el?.capabilities?.supportCIT,
	supportMIT: el?.capabilities?.supportMIT,
	cycleMetrics: el?.cycleMetrics,
	providerLogo: el?.providerLogo,
	providerLogoAlt: el?.providerLogoAlt,
	useDynamicDescriptor: el?.capabilities?.useDynamicDescriptor,
	useDynamicDescriptorSuffix: el?.capabilities?.useDynamicDescriptorSuffix,
	supportUDF: el?.capabilities?.supportUDF,
	supportDynamicDescriptorUrl: el?.capabilities?.supportDynamicDescriptorUrl,
	supportDynamicDescriptorAddress: el?.capabilities?.supportDynamicDescriptorAddress,
	processorPlatform: el?.processorPlatform,
	relatedMerchants: el?.relatedMerchants,
	relatedMerchantsCount: el?.relatedMerchantsCount,
	tier: el?.tier,
	caid: el?.caid,
	bin: el?.bin,
	sponsorBank: el?.sponsorBank,
	contactEmail: el?.contactEmail,
	contactPhone: el?.contactPhone,
	contactName: el?.contactName,
	quarantine_AddedOn: el?.cycleMetrics?.quarantine_AddedOn,
	configuration: el?.configuration,
}));

export const defaultProviderColumns: MRT_ColumnDef<IFormattedProviderProps>[] = [
// @ts-ignore
// 	{
// 		accessorKey: ProviderTableAccessorKeys.providerLogo,
// 		header: 'Provider Logo',
// 		enableSorting: false,
// 		Header: <span className="text-muted fw-bolder main-font-family">Provider Logo</span>,
// 		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
// 			return (
// 			<TableCell
// 				additionalClass="main-font-family text-black"
// 				density={table?.options?.state?.density}
// 				isTextOverflow={false}
// 			>
// 				<ProviderLayout providerId={row?.original?.id}>
// 					<img
// 						className="d-block mx-auto"
// 						src={renderedCellValue
// 							? renderedCellValue
// 							: toAbsoluteUrl('/media/flex-logo-small.svg')
// 					}
// 						height={20}
// 						width={30}
// 						alt={row?.original?.providerLogoAlt}
// 					/>
// 				</ProviderLayout>
// 			</TableCell>
// 		);
// 		}
// 	},
	{
		accessorKey: ProviderTableAccessorKeys.partner,
		header: 'Partner',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Partner</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<RelatedEntityNameLink id={renderedCellValue} type={RELATED_ENTITY_TYPE.PARTNER} />
			</TableCell>
		)
	},
	{
		accessorKey: ProviderTableAccessorKeys.name,
		header: 'Provider',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family fs-9">Provider</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<ProviderLayout providerId={row?.original?.id}>
					<div className="fc-text-overflow">
						{renderedCellValue ? renderedCellValue : '-'}
					</div>
				</ProviderLayout>
			</TableCell>
		)
	},
	{
		accessorKey: ProviderTableAccessorKeys.relatedMerchantsCount,
		header: 'Related merchants',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Related merchants</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedMerchants
					name={renderedCellValue}
					secondaryName='-'
					relatedMerchantsList={row?.original?.relatedMerchants}
					relatedMerchantsCount={renderedCellValue}
					additionalClass='fs-9'
				/>
			</TableCell>
		)
	},
	{
		accessorKey: ProviderTableAccessorKeys.merchantNumber,
		header: 'Merchant number',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Merchant number</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ProviderLayout providerId={row?.original?.id}>
					<div className="fc-text-overflow">
						{renderedCellValue ? renderedCellValue : '-'}
					</div>
				</ProviderLayout>
			</TableCell>
		)
	},
	{
		accessorKey: ProviderTableAccessorKeys.cardBrands,
		header: 'Card brands',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Card brands</span>,
		Cell: ({ renderedCellValue, table,row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<ProviderLayout providerId={row?.original?.id}>
					<ProviderCardBrands cardBrands={renderedCellValue}/>
					{/*{renderedCellValue ? renderedCellValue : '-'}*/}
				</ProviderLayout>
			</TableCell>
		)
	},

	{
		accessorKey: ProviderTableAccessorKeys.totalMonthlyCount,
		header: 'Total count',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Total count</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.totalTransactionCount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<ProviderLayout providerId={row?.original?.id}>
						{/*<ProviderMaxCount*/}
						{/*	count={renderedCellValue}*/}
						{/*	metric={metric >= 0 ? metric : null}*/}
						{/*/>*/}
						<TableProgressBar
							maxValue={renderedCellValue}
							metric={metric >= 0 ? metric : null}
							type={VALUE_TYPE.COUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.totalMonthlyAmount,
		header: 'Total amount',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Total amount</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.totalTransactionAmount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<ProviderLayout providerId={row?.original?.id}>
						{/*<ProviderMaxValue*/}
						{/*	value={renderedCellValue === null ? null : renderedCellValue /100}*/}
						{/*	metric={metric >= 0 ? metric / 100 : null}*/}
						{/*/>*/}
						<TableProgressBar
							maxValue={renderedCellValue === null ? null : renderedCellValue / 100}
							metric={metric >= 0 ? metric / 100 : null}
							type={VALUE_TYPE.AMOUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.totalDailyCount,
		header: 'Total daily count',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Total daily count</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.totalDailyTransactionCount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<ProviderLayout providerId={row?.original?.id}>
						<TableProgressBar
							maxValue={renderedCellValue}
							metric={metric >= 0 ? metric : null}
							type={VALUE_TYPE.COUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.totalDailyAmount,
		header: 'Total daily amount',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Total daily amount</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.totalDailyTransactionAmount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<ProviderLayout providerId={row?.original?.id}>
						<TableProgressBar
							maxValue={renderedCellValue === null ? null : renderedCellValue / 100}
							metric={metric >= 0 ? metric / 100 : null}
							type={VALUE_TYPE.AMOUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},

	{
		accessorKey: ProviderTableAccessorKeys.masterCard_MonthlyMaxTransactionsCount,
		header: 'MC monthly max transactions count',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">MC monthly max transactions count</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.masterCardCurrentTransactionsCount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<ProviderLayout providerId={row?.original?.id}>
						{/*<ProviderMaxCount*/}
						{/*	count={renderedCellValue}*/}
						{/*	metric={metric >= 0 ? metric : 0}*/}
						{/*/>*/}
						<TableProgressBar
							maxValue={renderedCellValue}
							metric={metric >= 0 ? metric : null}
							type={VALUE_TYPE.COUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.masterCard_MonthlyMaxValue,
		header: 'MC monthly max value',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">MC monthly max value</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.masterCardCurrentTransactionsValue;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<ProviderLayout providerId={row?.original?.id}>
						<TableProgressBar
							maxValue={renderedCellValue === null ? null : renderedCellValue / 100}
							metric={metric >= 0 ? metric / 100: null}
							type={VALUE_TYPE.AMOUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.visa_MonthlyMaxTransactionsCount,
		header: 'Visa transactions count',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Visa monthly max transactions count</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.visaCurrentTransactionsCount;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<ProviderLayout providerId={row?.original?.id}>
						<TableProgressBar
							maxValue={renderedCellValue}
							metric={metric >= 0 ? metric : null}
							type={VALUE_TYPE.COUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.visa_MonthlyMaxValue,
		header: 'Visa transactions value',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-9">Visa monthly max value</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const metric = row?.original?.cycleMetrics?.visaCurrentTransactionsValue;
			return (
				<TableCell
					additionalClass="main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<ProviderLayout providerId={row?.original?.id}>
						<TableProgressBar
							maxValue={renderedCellValue === null ? null : renderedCellValue / 100}
							metric={metric >= 0 ? metric / 100: null}
							type={VALUE_TYPE.AMOUNT}
							mainAdditionalClass="fs-9"
						/>
					</ProviderLayout>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ProviderTableAccessorKeys.supportMIT,
		header: 'MIT support',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-7">MIT support</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ProviderLayout providerId={row?.original?.id}>
					{renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-danger h-20px"/>}
				</ProviderLayout>
			</TableCell>
		)
	},
	{
		accessorKey: ProviderTableAccessorKeys.supportCIT,
		header: 'CIT support',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-7">CIT support</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ProviderLayout providerId={row?.original?.id}>
					{renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-danger h-20px"/>}
				</ProviderLayout>
			</TableCell>
		)
	},
	// {
	// 	accessorKey: ProviderTableAccessorKeys.sandbox,
	// 	header: 'Sandbox',
	// 	enableSorting: false,
	// 	Header: <span className="text-muted main-font-family fs-7">Sandbox</span>,
	// 	Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
	// 		<TableCell
	// 			additionalClass="main-font-family text-black"
	// 			density={table?.options?.state?.density}
	// 			isTextOverflow={false}
	// 		>
	// 			<ProviderLayout providerId={row?.original?.id}>
	// 				{renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-primary h-20px"/>}
	// 			</ProviderLayout>
	// 		</TableCell>
	// 	)
	// },
	{
		accessorKey: ProviderTableAccessorKeys.isActive,
		header: 'Active',
		enableSorting: false,
		Header: <span className="text-muted main-font-family fs-7">Active</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ProviderLayout providerId={row?.original?.id}>
					{renderedCellValue && <FontAwesomeIcon icon={faSquareCheck} width={20} className="text-danger h-20px"/>}
				</ProviderLayout>
			</TableCell>
		)
	},
];
